import type { FormatoDoPost, RedeDoPost, SituacaoDoPost } from './api.ts';

/**
 * Os nomes do calendário editorial (PRD §50), num lugar só: a grade, a lista e
 * a janela do post dizem a mesma coisa com a mesma palavra.
 *
 * ⚠️ **A cor de cada situação mora no CSS** (`estilos/marketing.css`, classes
 * `mkt-sit-*`), saída dos tokens. Aqui só o nome e o que ele quer dizer.
 */
export const SITUACOES_DO_POST: Array<{ id: SituacaoDoPost; nome: string; ajuda: string }> = [
  { id: 'ideia', nome: 'Ideia', ajuda: 'O tema existe; o texto, ainda não.' },
  { id: 'rascunho', nome: 'Rascunho', ajuda: 'Está sendo escrito.' },
  { id: 'aprovado', nome: 'Aprovado', ajuda: 'Pronto para sair.' },
  { id: 'publicado', nome: 'Publicado', ajuda: 'Está no ar.' },
];

export const FORMATOS_DO_POST: Array<{ id: FormatoDoPost; nome: string }> = [
  { id: 'carrossel', nome: 'Carrossel' },
  { id: 'imagem_unica', nome: 'Imagem única' },
  { id: 'texto', nome: 'Texto' },
];

export const REDES_DO_POST: Array<{ id: RedeDoPost; nome: string }> = [
  { id: 'instagram', nome: 'Instagram' },
  { id: 'linkedin', nome: 'LinkedIn' },
];

export const nomeDaSituacao = (s: SituacaoDoPost) => SITUACOES_DO_POST.find((x) => x.id === s)?.nome ?? s;
export const nomeDoFormato = (f: FormatoDoPost) => FORMATOS_DO_POST.find((x) => x.id === f)?.nome ?? f;
export const nomeDaRede = (r: RedeDoPost) => REDES_DO_POST.find((x) => x.id === r)?.nome ?? r;

/*
 * ⭐⭐ O CONTEÚDO DO POST — parte 2, 15/10/2026 (PRD §50.5).
 *
 * ⚠️ **As três funções abaixo têm gêmeas no servidor** (`api/src/lib/conteudo-do-post.ts`):
 * a tela junta e conta para copiar e para o contador andar a cada letra; o
 * servidor, para a conferência. São pequenas, e `npm run testar-marketing`
 * importa estas e confere que dizem o mesmo que as de lá.
 */

/** A legenda que se cola no Instagram: o texto, uma linha em branco e as hashtags. */
export function legendaComHashtags(legenda: string, hashtags: string): string {
  const l = legenda.trim();
  const h = hashtags.trim();
  return l && h ? `${l}\n\n${h}` : l || h;
}

/** As hashtags de um texto: `#` seguido de letra, número ou sublinhado. */
export function hashtagsDe(texto: string): string[] {
  return texto.match(/#[\p{L}\p{N}_]+/gu) ?? [];
}

/** Conta por caractere: o emoji é um, como a pessoa vê, e não dois. */
export const caracteres = (texto: string) => Array.from(texto).length;

/**
 * ⭐ **Copiar para colar na rede** — hoje a publicação é à mão (a parte 5 é que
 * publica sozinha). O aviso diz o que foi copiado, ou que não deu.
 *
 * ⚠️ Sem a área de transferência (navegador antigo, ou endereço sem https),
 * o aviso manda selecionar à mão, em vez de fingir que copiou.
 */
export function copiar(texto: string, avisar: (t: string, tom?: 'bom' | 'ruim') => void, copiado: string) {
  if (!navigator.clipboard) {
    avisar('Este navegador não deixou copiar. Selecione o texto e copie à mão.', 'ruim');
    return;
  }
  void navigator.clipboard
    .writeText(texto)
    .then(() => avisar(copiado))
    .catch(() => avisar('Não consegui copiar. Selecione o texto e copie à mão.', 'ruim'));
}

/** O nome de cada regra da conferência, como a tela o diz. */
export const NOME_DA_REGRA: Record<string, string> = {
  travessao: 'Travessão',
  negrito: 'Negrito',
  emoji: 'Emoji',
  promessa: 'Promessa de resultado',
  metodo: 'Fonte ou ferramenta',
  proibida: 'Palavra proibida',
  karen: 'A Karen',
  endereco: 'Endereço',
  contato: 'Contato',
  limite: 'Limite da rede',
  falta: 'Falta',
  /** ⭐ Parte 4: o texto que não cabe na arte, ou o caractere que a fonte da marca não tem. */
  arte: 'Na arte',
};
